import { useState } from "react";


/**
 * A component for displaying column filter options (input or select).
 * 
 * @param {Object} props - The props passed to the component.
 * @param {string} props.accessor - The column's unique identifier.
 * @param {string} props.filter - The current filter value for the column.
 * @param {Array} props.enumValues - Enum values available for filtering (optional).
 * @param {function} props.onFilterChange - Callback function to update the filter value.
 * @param {string} props.dataType - The data type of the column, used to customize the filter behavior.
 * 
 * @returns {JSX.Element | null} A select or input element for filtering columns based on dataType, or null if no filter is applicable.
 */
const ColumnFilter = ({ accessor, filter, enumValues, onFilterChange, dataType, data = null}) => {
  const [startDate, setStartDate] = useState(filter?.start || "");
  const [endDate, setEndDate] = useState(filter?.end || "");
  const [value, setValue] = useState(filter || "");

  const handleOnChange = (newValue) => {
    setValue(newValue);
    onFilterChange(accessor, { filter: newValue });
  };

  // No filter applicable
  if (dataType === "None") {
    return null;
  }

  // Dropdown for enum values
  if (Array.isArray(enumValues) && enumValues.length > 0) {
    return (
      <select
        value={value || ""}
        onChange={(e) => handleOnChange(e.target.value)}
      >
        <option value="">All</option>
        {enumValues.map((option, index) => (
          <option key={index} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  // Dropdown for tinyint (boolean-like) values
  if (dataType === "tinyint") {
    const booleanOptions = ["False", "True"];
    return (
      <select
        value={value || ""}
        onChange={(e) => handleOnChange(e.target.value)}
      >
        <option value="">All</option>
        {booleanOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (dataType === "date") {
    const today = new Date().toISOString().split("T")[0];

    const handleStartDateChange = (e) => {
      const newStartDate = e.target.value;
  
      if (newStartDate !== "" && newStartDate <= today) {
        if (endDate === "" && newStartDate !== "") {
          setEndDate(today);
        }
        
        setStartDate(newStartDate);
        onFilterChange(accessor, { filter: { start: newStartDate, end: endDate } });
      }
    };
  
    const handleEndDateChange = (e) => {
      const newEndDate = e.target.value;
      
      if (newEndDate !== "" && newEndDate >= startDate && newEndDate <= today) {
        setEndDate(newEndDate);
        onFilterChange(accessor, { filter: { start: startDate, end: newEndDate } });
      }
    };
  
    return (
      <div>
        <input
          type="date"
          value={startDate}
          onChange={handleStartDateChange}
          placeholder="Start Date"
          max={today}
        />
        <span> to </span>
        <input
          type="date"
          value={endDate}
          onChange={handleEndDateChange}
          placeholder="End Date"
          min={startDate} 
          max={today}
        />
        <button
          onClick={() => {
            setStartDate("");
            setEndDate("");
            handleOnChange("");
          }}
        >
          Clear
        </button>
      </div>
    );
  }

  if (dataType === "autoGenerate" && data) {
    const columnValues = data.data.map(row => row[accessor]).filter(value => value !== undefined);
  
    const uniqueColumnValues = [...new Set(columnValues)];
  
    return (
      <select
        value={value || ""}
        onChange={(e) => handleOnChange(e.target.value)}
      >
        <option value="">All</option>
        {uniqueColumnValues.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  // Default text input filter
  return (
    <input
      type="text"
      placeholder={`Filter ${accessor}`}
      value={value || ""}
      onChange={(e) => handleOnChange(e.target.value)}
    />
  );
};

export default ColumnFilter;
